import React from 'react';
import { Box, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import PanelBlock, { PanelEyebrow, Prose } from './PanelBlock';
import { LABOKLIN_RED } from '../../theme/laboklinTheme';
import type { NotDiagnostic } from '../../types/laboklin';

/**
 * What the page shows instead of a differential when the sample cannot support one.
 *
 * This panel exists because its absence was the defect. A sample the laboratory's own
 * indices call unusable never gets a case, and with nothing to represent that, the
 * differential panel sat on "Working through the differential…" until the tab was
 * closed. On report 2608R99119 a veterinarian saw exactly that on three runs in five -
 * and on all three the assistant had reached the right conclusion about a strongly
 * haemolysed sample. The judgement was sound; there was simply no way to say it.
 *
 * It REPLACES the differential rather than sitting above an empty one. A differential
 * built on artefacts is worse than none at all: the diagnosis engine ranks by how many
 * findings a disease explains and cannot see that a finding was an artefact, so on 119
 * it ranks the diseases haemolysis imitates - potassium 7.9 with a Na:K ratio of 17.7
 * reads as hypoadrenocorticism when it is a burst red cell.
 */

interface NotDiagnosticPanelProps {
  notDiagnostic: NotDiagnostic;
}

export const NotDiagnosticPanel: React.FC<NotDiagnosticPanelProps> = ({ notDiagnostic }) => {
  const { reason, invalidatedAnalytes, recommendation, advisory } = notDiagnostic;

  return (
    <PanelBlock>
      <PanelEyebrow label="Differential" state="Not run" />

      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <ReportProblemOutlinedIcon sx={{ fontSize: 18, color: LABOKLIN_RED, mt: '1px', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.75 }}>
            This sample is not diagnostic, so no differential was produced.
          </Typography>

          <Typography sx={{ fontSize: 13, lineHeight: 1.55, mb: 1.25 }} component="div"><Prose>{reason}</Prose></Typography>

          {invalidatedAnalytes.length > 0 && (
            <Box sx={{ mb: 1.25 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'text.secondary',
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Not interpretable on this sample
              </Typography>
              <Typography sx={{ fontSize: 13, lineHeight: 1.55 }}>
                {invalidatedAnalytes.join(', ')}
              </Typography>
            </Box>
          )}

          <Typography sx={{ fontSize: 13, lineHeight: 1.55, mb: advisory ? 1.25 : 0 }} component="div">
            <Prose>{recommendation}</Prose>
          </Typography>

          {/* The laboratory's own sentence, attributed. It is more specific than
              anything derivable from the indices - on 2608R99119 it names twenty-one
              affected analytes - and it is Laboklin speaking about their own sample,
              which carries more weight with a veterinarian than our paraphrase would. */}
          {advisory && (
            <Box
              sx={{
                borderLeft: `2px solid ${LABOKLIN_RED}`,
                pl: 1.25,
                py: 0.25,
              }}
            >
              <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: 'text.secondary' }}>
                {advisory}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5, fontStyle: 'italic' }}>
                — Laboklin, on this report
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </PanelBlock>
  );
};

export default NotDiagnosticPanel;
